import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import useAppSelector from '@sportson/core-web/hooks/useAppSelector';
import getProductStockStatus, { getPackageStockStatus } from '@sportson/core-web/utils/getProductStockStatus';
import { above } from '@sportson/core-web/utils/mediaqueries';
import styled from 'libs/styled';
import { useState, type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { GetStoresOnhandByPartNo } from '@sportson/core-web/libs/GrebbCommerceAPI/Products';
import Column from '@sportson/core-web/App/layouts/Column';
import { Price } from 'components/product/Price';
import { type IStormProduct } from 'components/product/types';
import { AddToCart } from 'components/productPage/AddToCart';
import ComingSoon from 'components/productPage/ComingSoon';
import discountInPercentage from '@sportson/core-web/utils/discountInPercentage';
import SoldOut from 'components/productPage/SoldOut';
import { PriceHistory } from '../information/PriceHistory';
import { type CommercialBadgeComponent } from './BadgeCommercial';
import { Badges } from './Badges';
import { Information } from './Information';
import { PartialPayment } from './PartialPayment';
import Stock from './Stock';
import StoreMessage from './StoreMessage';
import { StoreSelector } from './StoreSelector';
import { Usps } from './Usps';

interface StoreAvailability {
    id: number;
    on_hand_value: number;
}

const StockWrapper = styled(Column)`
    gap: 8px;
`;

const PriceWrapper = styled('div')`
    display: flex;
    flex-direction: column;

    ${above['tablet.sm']} {
        flex-direction: row;
        justify-content: space-between;
    }
`;

// TODO: Add types
export interface ProductInformationProps extends PropsWithChildren<{}> {
    selectedProduct: any;
    selectedProductVariants: any;
    totalPricing: any;
    isInStock: boolean;
    isBookable: boolean;
    isQuantityPackage: boolean;
    quantity: number;
    selectedStore: any;
    storesOnHand: any;
    bestHistoricalPriceIncVat: number;
    badges: CommercialBadgeComponent[];
    product: IStormProduct;
    isComingSoon?: boolean;
}

export const ProductInformation = ({
    children,
    selectedProduct,
    selectedProductVariants,
    totalPricing,
    isInStock,
    isBookable,
    isQuantityPackage,
    selectedStore,
    storesOnHand,
    bestHistoricalPriceIncVat,
    product,
    badges,
    isComingSoon,
}: ProductInformationProps) => {
    const { t } = useTranslation();
    const { name, manufacturer } = selectedProduct || {};
    const { isPackage } = selectedProduct;
    const selectedProductVariantValues: any = Object.values(selectedProductVariants);
    const [storeSelectorLabel, setStoreSelectorLabel] = useState('');
    const [storeStockStatus, setStoreStockStatus] = useState(0);
    const usps = useAppSelector(({ application }) => application.config.options?.productPage?.usps) ?? [];
    const overlayText = useAppSelector(
        ({ application }) => application.config.options?.productPage?.partialPaymentOptions,
    );
    const [inStockSelectedStore, setInStockSelectedStore] = useState(
        storesOnHand.find((s) => s.id === selectedStore?.storeId)?.on_hand_value > 0,
    );
    const { id: defaultPricelistId } = useSelector(({ application }) => application?.defaultPriceList) || {};

    const [storeAvailabilities, setStoreAvailabilities] = useState<StoreAvailability[]>([]);

    const getStoreOnhand = async (partNo) => {
        try {
            const response = await GetStoresOnhandByPartNo(partNo);
            setStoreAvailabilities(response.data[0]?.items ?? []);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        getStoreOnhand(selectedProduct.partNo);
    }, [selectedProduct.partNo]);

    useEffect(() => {
        if (storeAvailabilities.length > 0) {
            const selectedStoreAvailability = storeAvailabilities.find((store) => store.id === selectedStore?.storeId);
            setInStockSelectedStore((selectedStoreAvailability?.on_hand_value ?? 0) > 0);
        }
    }, [storeAvailabilities, selectedStore]);

    let stockStatus;
    let stockStatusStore;
    if (isPackage) {
        if (selectedProductVariantValues.every((packageProduct) => !!packageProduct)) {
            [stockStatus, stockStatusStore] = getPackageStockStatus(selectedProductVariantValues);
        }
    } else {
        stockStatus = getProductStockStatus(selectedProduct?.onHand);
        stockStatusStore = stockStatus.stock <= 0 ? 0 : getProductStockStatus(selectedProduct?.onHandStore);
    }

    const { vatRate, priceExVat, priceIncVat } = totalPricing;
    const standardPriceExVat =
        totalPricing.priceExVat > totalPricing.standardPriceExVat
            ? totalPricing.priceExVat
            : totalPricing.standardPriceExVat;

    const priceListId = totalPricing.priceListId || selectedProduct.priceListId;

    // If priceListId or priceListCode is passed, check dem against default pricelist
    // to determine if product actually is on sale
    let hasCampaignPriceList = false;
    if (priceListId) {
        hasCampaignPriceList = priceListId !== defaultPricelistId;
    }

    const onSale =
        hasCampaignPriceList &&
        !!(
            standardPriceExVat &&
            (standardPriceExVat > priceExVat || (discountInPercentage(standardPriceExVat, priceExVat) || 0) > 1)
        );

    const flagIdSeed = product.flag_id_seed ?? product.flagIdSeed;
    const flags = flagIdSeed.split(',');
    const isSecondHand = flags.includes(`${process.env.REACT_APP_STORM_FLAG_SECOND_HAND}`);

    const selectedProductVariantIsComing = selectedProductVariants[product.id]?.statusId === 2;

    const [isSecondHandAndSold, setIsSecondHandAndSold] = useState(isSecondHand && !isInStock);
    const [isAddToCartButtonDisabled, setIsAddToCartButtonDisabled] = useState(
        isSecondHand ? !!selectedStore && !inStockSelectedStore : selectedProductVariantIsComing,
    );

    useEffect(() => {
        setIsSecondHandAndSold(isSecondHand && !isInStock);
    }, [isSecondHand, isInStock]);

    useEffect(() => {
        setIsAddToCartButtonDisabled(
            isSecondHand ? !!selectedStore && !inStockSelectedStore : selectedProductVariantIsComing,
        );
    }, [isSecondHand, selectedStore, inStockSelectedStore, selectedProductVariantIsComing]);

    return (
        <Column gap="16px" p={['32px 12px 0px 12px', null, '24px 12px 0', '0 40px']}>
            <Column gap={['6px', null, null, '24px']}>
                <Information manufacturerName={manufacturer?.name} productName={name} />
                {badges && <Badges badges={badges} />}
                {isComingSoon && <ComingSoon />}
                {isSecondHandAndSold && !isComingSoon && <SoldOut />}
                {!isSecondHandAndSold && !isComingSoon && children}
                {!isSecondHandAndSold && !isComingSoon && !selectedProductVariantIsComing && (
                    <StockWrapper>
                        <StoreSelector
                            inStockStore={inStockSelectedStore}
                            label={storeSelectorLabel}
                            selectedStore={selectedStore}
                            stockStatus={storeStockStatus}
                            stores={storesOnHand}
                            onlyShowInStock={isSecondHand}
                        />
                        {isSecondHand && <StoreMessage product={product} inStockStore={inStockSelectedStore} />}
                        <Stock
                            inStockStore={inStockSelectedStore}
                            product={selectedProduct}
                            stockStatus={stockStatus}
                            stockStatusStore={stockStatusStore}
                            isSecondHand={isSecondHand}
                        />
                    </StockWrapper>
                )}

                {!isSecondHandAndSold && !isComingSoon && !!standardPriceExVat && !!vatRate && (
                    <PriceWrapper paddingTop="16px">
                        <Price
                            priceExVat={priceExVat}
                            priceListId={priceListId}
                            standardPriceExVat={standardPriceExVat}
                            vatRate={vatRate}
                        />
                        {onSale && (
                            <PriceHistory
                                label={t('pdp.info.price_history')}
                                priceHistory={bestHistoricalPriceIncVat}
                            />
                        )}
                    </PriceWrapper>
                )}
            </Column>
            {!isSecondHandAndSold && !isComingSoon && (
                <>
                    <AddToCart
                        fullWidth
                        showCartIcon
                        height="56px"
                        size="large"
                        inStock={isInStock}
                        isBookable={isBookable}
                        isDisabled={isAddToCartButtonDisabled}
                        productId={product.id}
                        isPackage={isPackage}
                        isQuantityPackage={isQuantityPackage}
                        product={selectedProduct}
                        selectedProductVariants={selectedProductVariants}
                        buttonText={selectedProductVariantIsComing ? 'pdp.productStatusComing' : 'pdp.addToCart'}
                    />

                    <Column gap={['16px', null, null, '24px']}>
                        <PartialPayment
                            price={priceIncVat}
                            overlayTextContent={overlayText?.overlayDisplayTextContent}
                        />
                        {!isSecondHand && <Usps usps={usps} />}
                    </Column>
                </>
            )}
        </Column>
    );
};
